import { Transforms, Editor, Node } from "slate";

/**
 * Check if a path exists in the editor's document tree.
 * Prevents "Cannot find a descendant at path [X]" crashes when
 * backend state drifts from the frontend Yjs editor state.
 */
const pathExists = (editor, path) => {
  try {
    if (!path || !Array.isArray(path) || path.length === 0) return false;
    if (!editor?.children || editor.children.length === 0) return false;
    Node.get(editor, path);
    return true;
  } catch {
    return false;
  }
};

/**
 * Check if a path's parent exists (for insert operations that create new nodes).
 * Insert at [N] requires the root to exist; insert at [0, 1] requires [0] to exist.
 */
const parentPathExists = (editor, path) => {
  if (!path || !Array.isArray(path)) return false;
  if (path.length <= 1) {
    // Root-level insert — always valid as long as editor exists
    return !!editor?.children;
  }
  return pathExists(editor, path.slice(0, -1));
};

/**
 * Applies canvas operations to a Slate editor
 * @param {Object} editor - The Slate editor instance
 * @param {Object} canvasOperations - The canvas operations payload
 * @param {string} lastAppliedChangesetID - The last changeset ID that was already applied
 * @returns {Object} - Returns object with success status and the last applied changeset ID
 */
export const applyCanvasOperations = (
  editor,
  canvasOperations,
  lastAppliedChangesetID = null
) => {
  if (!editor || !canvasOperations) {
    return { success: false, lastChangesetID: lastAppliedChangesetID };
  }

  // Handle both formats: new format with operations array and old format with operationsByID
  let operationsToProcess = [];
  let finalChangesetID = null;

  if (
    canvasOperations.operations &&
    Array.isArray(canvasOperations.operations)
  ) {
    // New format: operations is an array with changesetId in each item
    const { operations, currentChangeSetID } = canvasOperations;

    // Filter operations to only include those after lastAppliedChangesetID
    if (lastAppliedChangesetID) {
      // Find all unique changeset IDs in order
      const changesetIds = [...new Set(operations.map((op) => op.changesetId))];
      const lastIndex = changesetIds.indexOf(lastAppliedChangesetID);

      if (lastIndex !== -1 && lastIndex === changesetIds.length - 1) {
        // All changesets have been applied

        return { success: true, lastChangesetID: lastAppliedChangesetID };
      }

      // Get changesets that come after the last applied one
      const newChangesets =
        lastIndex === -1 ? changesetIds : changesetIds.slice(lastIndex + 1);

      // Filter operations to only include new changesets
      operationsToProcess = operations.filter((op) =>
        newChangesets.includes(op.changesetId)
      );
    } else {
      operationsToProcess = operations;
    }

    // Set the final changeset ID
    finalChangesetID =
      currentChangeSetID ||
      (operationsToProcess.length > 0
        ? operationsToProcess[operationsToProcess.length - 1].changesetId
        : lastAppliedChangesetID);
  } else if (canvasOperations.operationsByID) {
    // Old format: operationsByID object
    const { operationsByID, currentChangeSetID } = canvasOperations;

    // Get all changeset IDs in order
    const changesetIDs = Object.keys(operationsByID);
    if (changesetIDs.length === 0) {
      return { success: false, lastChangesetID: lastAppliedChangesetID };
    }

    // Find the index of the last applied changeset
    let startIndex = 0;
    if (lastAppliedChangesetID) {
      const lastIndex = changesetIDs.indexOf(lastAppliedChangesetID);
      if (lastIndex !== -1) {
        startIndex = lastIndex + 1;
      }
    }

    // If all changesets have already been applied
    if (startIndex >= changesetIDs.length) {
      return { success: true, lastChangesetID: lastAppliedChangesetID };
    }

    // Convert to operations array format
    const changesetsToApply = changesetIDs.slice(startIndex);
    changesetsToApply.forEach((changesetID) => {
      const ops = operationsByID[changesetID];
      if (ops && Array.isArray(ops)) {
        ops.forEach((op) => {
          operationsToProcess.push({ changesetId: changesetID, operation: op });
        });
      }
    });

    finalChangesetID =
      currentChangeSetID ||
      changesetsToApply[changesetsToApply.length - 1] ||
      lastAppliedChangesetID;
  } else {
    return { success: false, lastChangesetID: lastAppliedChangesetID };
  }

  // Apply the operations
  if (operationsToProcess.length === 0) {
    return { success: true, lastChangesetID: lastAppliedChangesetID };
  }

  let lastSuccessfulChangesetID = lastAppliedChangesetID;

  try {
    console.log("[canvasOps] Applying operations:", {
      count: operationsToProcess.length,
      editorChildrenCount: editor.children?.length,
      editorChildrenTypes: editor.children?.map((c) => c.type),
      lastAppliedChangesetID,
      finalChangesetID,
      operations: operationsToProcess.map((item) => {
        const op = item.operation || item;
        return {
          type: op.type,
          path: op.path,
          changesetId: item.changesetId,
          hasText: !!op.text,
          hasNode: !!op.node,
        };
      }),
    });

    // Guard: ensure editor has at least a minimal valid state before applying operations.
    // After canvas_init, the Yjs editor may still have children: [] before sync completes.
    // Insert a default paragraph so Slate's normalizer doesn't crash post-withoutNormalizing.
    if (!editor.children || editor.children.length === 0) {
      console.warn("[canvasOps] Editor has empty children — inserting default paragraph before applying ops");
      Transforms.insertNodes(editor, { type: "paragraph", children: [{ text: "" }] }, { at: [0] });
    }

    Editor.withoutNormalizing(editor, () => {
      operationsToProcess.forEach((item) => {
        const operation = item.operation || item; // Handle both wrapped and unwrapped operations
        console.log("[canvasOps] Applying operation:", {
          type: operation.type,
          path: operation.path,
          changesetId: item.changesetId,
          editorChildrenCount: editor.children?.length,
        });
        applyOperation(editor, operation);
        if (item.changesetId) {
          lastSuccessfulChangesetID = item.changesetId;
        }
      });
    });

    return {
      success: true,
      lastChangesetID: finalChangesetID || lastSuccessfulChangesetID,
      appliedCount: operationsToProcess.length,
    };
  } catch (error) {
    console.error("Error applying canvas operations:", error);
    return {
      success: false,
      lastChangesetID: lastSuccessfulChangesetID,
      error: error.message,
    };
  }
};

/**
 * Applies a single operation to the editor
 * @param {Object} editor - The Slate editor instance
 * @param {Object} operation - The operation to apply
 */
const applyOperation = (editor, operation) => {
  const { type, path, text, node, properties, offset, position } = operation;

  switch (type) {
    case "insert_text":
      handleInsertText(editor, path, offset, text);
      break;

    case "remove_text":
      handleRemoveText(editor, path, offset, text);
      break;

    case "insert_node":
      handleInsertNode(editor, path, node);
      break;

    case "remove_node":
      handleRemoveNode(editor, path);
      break;

    case "set_node":
      handleSetNode(editor, path, properties);
      break;

    case "split_node":
      handleSplitNode(editor, path, position, properties);
      break;

    case "merge_node":
      handleMergeNode(editor, path, position);
      break;

    default:
      console.warn("Unknown operation type:", type);
  }
};

/**
 * Handles insert_text operation
 * @param {Object} editor - The Slate editor instance
 * @param {Array} path - The path where to insert text
 * @param {number} offset - The offset within the text node
 * @param {string} text - The text to insert
 */
const handleInsertText = (editor, path, offset, text) => {
  if (!text) return;

  try {
    if (path && Array.isArray(path)) {
      // Guard: verify the text node exists at this path
      if (!pathExists(editor, path)) {
        console.warn("Skipping insert_text — path does not exist:", path);
        return;
      }
      const point = { path, offset: offset || 0 };
      Transforms.insertText(editor, text, { at: point });
    } else {
      Transforms.insertText(editor, text);
    }
  } catch (error) {
    console.error("Error inserting text:", error);
  }
};

/**
 * Handles remove_text operation
 * @param {Object} editor - The Slate editor instance
 * @param {Array} path - The path where to remove text
 * @param {number} offset - The starting offset
 * @param {string} text - The text to remove (for length calculation)
 */
const handleRemoveText = (editor, path, offset, text) => {
  try {
    if (path && Array.isArray(path)) {
      // Guard: verify the text node exists at this path
      if (!pathExists(editor, path)) {
        console.warn("Skipping remove_text — path does not exist:", path);
        return;
      }
      const startPoint = { path, offset: offset || 0 };
      const endPoint = { path, offset: (offset || 0) + (text ? text.length : 0) };
      const range = {
        anchor: startPoint,
        focus: endPoint,
      };

      Transforms.delete(editor, { at: range });
    } else {
      Transforms.delete(editor, { distance: text ? text.length : 1 });
    }
  } catch (error) {
    console.error("Error removing text:", error);
  }
};

/**
 * Handles insert_node operation
 * @param {Object} editor - The Slate editor instance
 * @param {Array} path - The path where to insert the node
 * @param {Object} node - The node to insert
 */
const handleInsertNode = (editor, path, node) => {
  try {
    if (!node) return;

    const options = {};
    if (path && Array.isArray(path)) {
      // Guard: verify parent path exists for nested inserts
      if (!parentPathExists(editor, path)) {
        console.warn("Skipping insert_node — parent path does not exist:", path,
          "editor children:", editor?.children?.length);
        return;
      }
      options.at = path;
    }

    Transforms.insertNodes(editor, node, options);
  } catch (error) {
    console.error("Error inserting node:", error);
  }
};

/**
 * Handles remove_node operation
 * @param {Object} editor - The Slate editor instance
 * @param {Array} path - The path of the node to remove
 */
const handleRemoveNode = (editor, path) => {
  try {
    if (path && Array.isArray(path)) {
      // Guard: verify the node actually exists before removing
      // (prevents "Cannot find a descendant at path [X]" when backend/frontend state diverge)
      if (!pathExists(editor, path)) {
        console.warn("Skipping remove_node — path does not exist:", path,
          "editor children:", editor?.children?.length);
        return;
      }
      Transforms.removeNodes(editor, { at: path });
    }
  } catch (error) {
    console.error("Error removing node:", error);
  }
};

/**
 * Handles set_node operation
 * @param {Object} editor - The Slate editor instance
 * @param {Array} path - The path of the node to update
 * @param {Object} properties - The properties to set
 */
const handleSetNode = (editor, path, properties) => {
  try {
    if (!properties) return;

    const options = {};
    if (path && Array.isArray(path)) {
      if (!pathExists(editor, path)) {
        console.warn("Skipping set_node — path does not exist:", path);
        return;
      }
      options.at = path;
    }

    Transforms.setNodes(editor, properties, options);
  } catch (error) {
    console.error("Error setting node properties:", error);
  }
};

/**
 * Handles split_node operation
 * @param {Object} editor - The Slate editor instance
 * @param {Array} path - The path where to split the node
 * @param {number} position - The position within the node to split at
 * @param {Object} properties - Optional properties for the new node
 */
const handleSplitNode = (editor, path, position, properties) => {
  try {
    const options = {};
    
    if (path && Array.isArray(path)) {
      if (position !== undefined) {
        const point = { path, offset: position };
        options.at = point;
      } else {
        options.at = path;
      }
    }
    
    if (properties) {
      options.properties = properties;
    }

    Transforms.splitNodes(editor, options);
  } catch (error) {
    console.error("Error splitting node:", error);
  }
};

/**
 * Handles merge_node operation
 * @param {Object} editor - The Slate editor instance
 * @param {Array} path - The path of the node to merge
 * @param {number} position - Optional position for the merge
 */
const handleMergeNode = (editor, path, position) => {
  try {
    const options = {};
    
    if (path && Array.isArray(path)) {
      if (position !== undefined) {
        const point = { path, offset: position };
        options.at = point;
      } else {
        options.at = path;
      }
    }

    Transforms.mergeNodes(editor, options);
  } catch (error) {
    console.error("Error merging nodes:", error);
  }
};


/**
 * Applies all operations from multiple change sets
 * @param {Object} editor - The Slate editor instance
 * @param {Object} canvasOperations - The canvas operations payload
 * @param {Array} changeSetOrder - Optional array specifying the order of change sets to apply
 * @returns {boolean} - Returns true if all operations were applied successfully
 */
export const applyAllCanvasOperations = (
  editor,
  canvasOperations,
  changeSetOrder = null
) => {
  if (!editor || !canvasOperations) return false;

  const { operationsByID } = canvasOperations;
  if (!operationsByID) return false;

  // Determine the order of change sets to apply
  const changeSets = changeSetOrder || Object.keys(operationsByID);

  try {
    Editor.withoutNormalizing(editor, () => {
      changeSets.forEach((changeSetID) => {
        const operations = operationsByID[changeSetID];
        if (operations && Array.isArray(operations)) {
          operations.forEach((operation) => {
            applyOperation(editor, operation);
          });
        }
      });
    });
    return true;
  } catch (error) {
    console.error("Error applying all canvas operations:", error);
    return false;
  }
};

export default {
  applyCanvasOperations,
  applyAllCanvasOperations,
};
